import { MutationTree, ActionTree, Module, GetterTree } from 'vuex';
import { RootState, OptionsState, Product, ProductsMappedByID, ProductWithoutHierarchy, AsOfType, AsOf, AsOfData, AsOfRelativeUnit } from './../types';
import FRTBApi from '@/api/frtb.api';
import _ from 'lodash';
import Env from '@/services/env.service';
import utilsService from '@/services/utils.service';

const determineProductHierarchy = (obj: any): ProductsMappedByID => {
  const vals = Object.values(obj);
  // Determine product level and relations
  const newVals = vals.map( (prod: any) => {
    prod.level = 1;
    prod.directChildren = [];
    prod.displayHierarchy = '';
    if (prod.display.indexOf('Securities and listed derivatives') > -1) {
      prod.level = prod.id.length;
      if (prod.level > 1 && prod.display.indexOf(' - ')) {
        const arr = prod.display.split(' - ');
        prod.displayHierarchy = arr[arr.length - 1];
      }
      prod.directChildren = vals.filter( (contender: any) => {
        return (contender.id.length === (prod.level + 1)) && (contender.id.substr(0, prod.level) === prod.id);
      }).map((contender: any) => contender.id);

      if (prod.level > 1) {
        prod.parent = prod.id.substr(0, prod.level - 1);
      }

      prod.selected = 'Unselected';
    }

    return prod;
  });

  const newObj: ProductsMappedByID = _.keyBy(newVals, 'id');
  return newObj;
};

const mapProductsById = (opts: any = {}, productsMap: any): ProductsMappedByID => {
  const obj: any = {};

  if (opts.types) {
    opts.types = _.orderBy(opts.types, (o: any) => {
      return productsMap[o.id] || o.id;
    });
    opts.types.forEach((item: any) => {
      const product: ProductWithoutHierarchy = {
        venues: item.sources,
        id: item.id,
        default_bucketing_set: item.default_bucketing_set,
        display: productsMap[item.id] || item.id
      };

      if (product.display.match(RegExp(Env.get('VUE_APP_PRODUCT_MATCH'))) && product.display.substr(0, 5) !== 'TR - ') {
        obj[item.id] = product;
      }
    });
  }

  const fullObjects = determineProductHierarchy(obj);
  return fullObjects;
};

const state: OptionsState = {
  options: {},
  productsDisplayMap: {},
  attributesDisplayMap: {},
  venuesDisplayMap: {},
  productsById: {},
  minAsOf: undefined,
  maxAsOf: undefined,
  asOf: {
    type: AsOfType.Relative,
    data: {
      count: 0,
      unit: AsOfRelativeUnit.Day,
    }
  }
};

const getters: GetterTree<OptionsState, RootState> = {
  getAsOf(optionsState): AsOf {
    return optionsState.asOf;
  },
  getAsOfDate(optionsState): Date {
    return utilsService.convertAsOfToDate(optionsState.asOf);
  },
  getAsOfRelative(optionsState): AsOfData {
    return utilsService.getAsOfRelative(optionsState.asOf);
  },
  getMinTime(optionsState): number | undefined {
    if (optionsState.minAsOf) {
      try {
        return new Date(optionsState.minAsOf).getTime();
      } catch (e) {
        return undefined;
      }
    }
    return undefined;
  },
  getMaxTime(optionsState): number | undefined {
    if (optionsState.maxAsOf) {
      try {
        return new Date(optionsState.maxAsOf).getTime();
      } catch (e) {
        return undefined;
      }
    }
    return Date.now();
  }
};

const mutations: MutationTree<OptionsState> = {
  setOptions(optionsState, resOptions?: any[]) {
    optionsState.options = resOptions || [];
  },
  setProductsDisplayMap(optionsState, displayMap?: any) {
    optionsState.productsDisplayMap = displayMap || {};
  },
  setVenuesDisplayMap(optionsState, displayMap?: any) {
    optionsState.venuesDisplayMap = displayMap || {};
  },
  setAttributesDisplayMap(optionsState, attributesDisplayMap?: any) {
    optionsState.attributesDisplayMap = attributesDisplayMap || {};
  },
  setProductsById(optionsState, productsById?: any) {
    optionsState.productsById = productsById || {};
  },
  setAsOf(optionsState, asOf: AsOf) {
    optionsState.asOf = asOf;
  },
  setMaxAsOf(optionsState, maxAsOf?: Date) {
    optionsState.maxAsOf = maxAsOf || undefined;
  },
  setMinAsOf(optionsState, minAsOf?: Date) {
    optionsState.minAsOf = minAsOf || undefined;
  }
};

const actions: ActionTree <OptionsState, RootState> = {
  async getOptions(context) {
    if (!_.isEmpty(context.state.options)) {
      return context.state.options;
    } else {
      try {
        const res = await FRTBApi.getOptions();
        context.commit('setProductsDisplayMap', res.display_names.types);
        context.commit('setAttributesDisplayMap', res.display_names.attributes);
        context.commit('setVenuesDisplayMap', res.display_names.sources);
        const productsById = mapProductsById(res, context.state.productsDisplayMap);
        context.commit('setProductsById', productsById);
        const asOf: AsOf = { type: AsOfType.Relative, data: { count: 4, unit: AsOfRelativeUnit.Week } };
        context.commit('setAsOf', asOf);
        context.commit('setOptions', res);
        context.commit('setMaxAsOf', res.max_as_of);
        context.commit('setMinAsOf', res.min_as_of);
      } catch (e) {
        context.commit('setToastError', 'Error getting options. Please sign out and in.');
      }
    }
  }
};

export const options: Module<OptionsState, RootState> = {
  getters,
  state,
  mutations,
  actions
};
